import {last, memoize, find} from 'lodash';
import getNormalTrackLineRequest from './getTrackLineRequests/getNormalRequest';

interface SnapTrackPointProps {
    point: TrackPoint;
    track: Track;
    project: MapProject;
    interaction: MapInteraction | undefined;
}

// We use `memoizedFetchLineEnd` and `memoizedFetchLineStart` to ensure that the `Promise`s returned
// by `snapTrackPoint` are memoized with respect to the output of `getNormalTrackLineRequest`.
// We do not need to memoize the plain `GeoJSON.Point` objects because `getSyncPromise` will not
// try to cache them.
const memoizedFetchLineEnd = memoize(async (
    geometry: Promise<GeoJSON.LineString>
): Promise<GeoJSON.Point> => {
    return {type: 'Point', coordinates: last((await geometry).coordinates)!};
});

const memoizedFetchLineStart = memoize(async (
    geometry: Promise<GeoJSON.LineString>
): Promise<GeoJSON.Point> => {
    return {type: 'Point', coordinates: (await geometry).coordinates[0]};
});

export default function snapTrackPoint({
    point,
    track,
    project,
    interaction,
}: SnapTrackPointProps): GeoJSON.Point | Promise<GeoJSON.Point> {

    if (point.properties.trackLocalId !== track.properties.trackLocalId) throw new Error();
    if (!track.isGeometryLoaded) return point.geometry;

    const draggingPointLocalId = interaction && interaction.subtype === 'draggingPoint' ?
        interaction.pointLocalId :
        undefined;

    const prevPoint = getPrevPoint(track, point);
    if (prevPoint) {
        const prevLine = getNormalTrackLineRequest({
            track,
            project,
            draggingPointLocalId,
            fromPoint: prevPoint,
            toPoint: point
        });
        if (prevLine.type === 'async') {
            return memoizedFetchLineEnd(prevLine.geometry);
        }
    }

    const nextPoint = getNextPoint(track, point);
    if (nextPoint) {
        const nextLine = getNormalTrackLineRequest({
            track,
            project,
            draggingPointLocalId,
            fromPoint: point,
            toPoint: nextPoint,
        });
        if (nextLine.type === 'async') {
            return memoizedFetchLineStart(nextLine.geometry);
        }
    }

    return point.geometry;
}

function getNextPoint(track: Track, point: TrackPoint) {
    return track.isGeometryLoaded ?
        find(track.points, p => p.properties.order === point.properties.order + 1) :
        undefined;
}

function getPrevPoint(track: Track, point: TrackPoint) {
    return track.isGeometryLoaded ?
        find(track.points, p => p.properties.order === point.properties.order - 1) :
        undefined;
}
